import React from 'react';
import {Vector3} from './types.ts';
import {FormGroup, Label, Input, Button} from 'reactstrap';
import SpatialCoordinateEditor from './SpatialCoordinateEditor.tsx';
import useDraft from './useDraft.ts';

interface TubeEditorProps {
    path: Vector3[];
    radius?: number | null;
    tessellation?: number | null;
    cap?: number | null;
    onPathChange: (path: Vector3[]) => void;
    onRadiusChange: (radius: number | null) => void;
    onTessellationChange: (tessellation: number | null) => void;
    onCapChange: (cap: number | null) => void;
}

const TubeEditor: React.FC<TubeEditorProps> = ({
    path,
    radius,
    tessellation,
    cap,
    onPathChange,
    onRadiusChange,
    onTessellationChange,
    onCapChange
}) => {
    const [currentPath, setCurrentPath] = useDraft<Vector3[]>(path);

    const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        onRadiusChange(isNaN(value) ? null : value);
    };

    const handleTessellationChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = parseInt(e.target.value, 10);
        onTessellationChange(isNaN(value) ? null : value);
    };

    const handleCapChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        onCapChange(isNaN(value) ? null : value);
    };

    const handlePathChange = (index: number, coordinate: Vector3) => {
        const updatedPath = [...currentPath];
        updatedPath[index] = coordinate;
        setCurrentPath(updatedPath);
    };

    const addPathPoint = () => {
        const newPoint: Vector3 = {x: 0, y: 0, z: 0};
        setCurrentPath([...currentPath, newPoint]);
    };

    const removePathPoint = (index: number) => {
        const updatedPath = currentPath.filter((_, i) => i !== index);
        setCurrentPath(updatedPath);
    };

    return (
        <>
            <FormGroup>
                <Label>Radius:</Label>
                <Input
                    type="number"
                    step="0.05"
                    min="0"
                    value={radius ?? ''}
                    placeholder="0.1"
                    onChange={handleRadiusChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Sides:</Label>
                <Input
                    type="number"
                    step="1"
                    min="3"
                    value={tessellation ?? ''}
                    placeholder="16"
                    onChange={handleTessellationChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Cap (0 open, 3 both ends closed):</Label>
                <Input
                    type="number"
                    step="1"
                    min="0"
                    max="3"
                    value={cap ?? ''}
                    placeholder="0"
                    onChange={handleCapChange}
                />
            </FormGroup>
            <FormGroup>
                <Label>Path:</Label>
                {currentPath.map((point, index) => (
                    <div key={index}>
                        <SpatialCoordinateEditor
                            label={`Point ${index + 1}`}
                            coordinate={point}
                            onChange={coordinate =>
                                handlePathChange(index, coordinate)
                            }
                        />
                        <Button
                            color="danger"
                            onClick={() => removePathPoint(index)}
                            disabled={currentPath.length <= 2}
                        >
                            Remove Point
                        </Button>
                    </div>
                ))}
                <Button color="primary" onClick={addPathPoint}>
                    Add Point
                </Button>
                <Button
                    color="success"
                    onClick={() => onPathChange(currentPath)}
                >
                    Apply Path Changes
                </Button>
            </FormGroup>
        </>
    );
};

export default TubeEditor;
